/**
 * The signed list of API base URLs a player app may fail over between.
 *
 * svc-setting signs, the player bundle verifies with a public key baked in at
 * build time. The signature is not optional: the list arrives from whichever host
 * still answers, so an unsigned list would let anyone holding ONE host point every
 * player at a phishing API.
 *
 * Wire shape: `{ payload, signature }`. `payload` is the exact JSON string that was
 * signed (so no canonicalisation is needed to verify it); `signature` is base64
 * Ed25519 over its UTF-8 bytes. Keys are base64 DER — PKCS#8 private, SPKI public.
 *
 * Payload v2 carries two instants. `updatedAt` is when an operator last saved the
 * list; `issuedAt` is when the server signed this copy, and it signs a fresh copy
 * on every read. A client refuses a copy issued too long ago
 * (`EDGE_HOSTS_MAX_AGE_MS`), so a captured list cannot be replayed at a first-time
 * visitor forever, and `environment` stops a list signed for one deployment being
 * accepted by another that happens to share the key. v1 payloads (no
 * `environment`, `issuedAt` = save time) still verify so a list a browser stored
 * before v2 is not thrown away; what a client does with one is its own policy.
 *
 * WebCrypto only, no Node APIs: this module runs in the browser as well.
 */

export const EDGE_HOSTS_VERSION = 2;
export const EDGE_HOSTS_MAX = 20;
/** Oldest signed copy a client accepts from the network. The server re-signs on every read. */
export const EDGE_HOSTS_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

const ENVIRONMENT_MAX = 32;

export interface SignedEdgeHosts {
  payload: string;
  signature: string;
}

export interface EdgeHostsPayload {
  v: 1 | 2;
  /** Deployment the list was signed for; null only on a legacy v1 payload. */
  environment: string | null;
  hosts: string[];
  /** ISO instant an operator last saved the list. */
  updatedAt: string;
  /** ISO instant this copy was signed. */
  issuedAt: string;
}

export interface EdgeHostsToSign {
  environment: string;
  hosts: readonly string[];
  updatedAt: Date;
  issuedAt: Date;
}

const LOOPBACK = new Set(['localhost', '127.0.0.1', '[::1]']);

/**
 * One API base URL in canonical form, or null when it is not acceptable: absolute,
 * https (http only on loopback, for local dev), no credentials, query or fragment,
 * no trailing slash.
 */
export function normaliseEdgeHost(raw: string): string | null {
  let url: URL;
  try {
    url = new URL(raw.trim());
  } catch {
    return null;
  }
  const secure = url.protocol === 'https:' || (url.protocol === 'http:' && LOOPBACK.has(url.hostname));
  if (!secure || url.username || url.password || url.search || url.hash) return null;
  return `${url.origin}${url.pathname.replace(/\/+$/, '')}`;
}

/** The deduplicated canonical list, or null when any entry is invalid or the size is out of range. */
export function normaliseEdgeHosts(raw: readonly string[]): string[] | null {
  const hosts: string[] = [];
  for (const entry of raw) {
    const host = normaliseEdgeHost(entry);
    if (host === null) return null;
    if (!hosts.includes(host)) hosts.push(host);
  }
  return hosts.length === 0 || hosts.length > EDGE_HOSTS_MAX ? null : hosts;
}

function toBase64(bytes: ArrayBuffer): string {
  return btoa(String.fromCharCode(...new Uint8Array(bytes)));
}

function fromBase64(value: string): ArrayBuffer {
  return Uint8Array.from(atob(value), (c) => c.charCodeAt(0)).buffer as ArrayBuffer;
}

export async function signEdgeHosts(privateKeyPkcs8: string, list: EdgeHostsToSign): Promise<SignedEdgeHosts> {
  const payload = JSON.stringify({
    v: EDGE_HOSTS_VERSION,
    environment: list.environment,
    hosts: list.hosts,
    updatedAt: list.updatedAt.toISOString(),
    issuedAt: list.issuedAt.toISOString(),
  });
  const key = await crypto.subtle.importKey('pkcs8', fromBase64(privateKeyPkcs8), { name: 'Ed25519' }, false, ['sign']);
  const signature = await crypto.subtle.sign('Ed25519', key, new TextEncoder().encode(payload));
  return { payload, signature: toBase64(signature) };
}

const isInstant = (value: unknown): value is string => typeof value === 'string' && !Number.isNaN(Date.parse(value));

/**
 * The payload when the signature verifies under `publicKeySpki` AND the content is
 * well formed; null for anything else. Never throws: a bad list must be dropped,
 * not crash the caller. Freshness and environment are the caller's to judge.
 */
export async function verifyEdgeHosts(
  publicKeySpki: string,
  doc: unknown,
): Promise<EdgeHostsPayload | null> {
  try {
    const { payload, signature } = doc as SignedEdgeHosts;
    if (typeof payload !== 'string' || typeof signature !== 'string' || signature.length === 0) return null;
    const key = await crypto.subtle.importKey('spki', fromBase64(publicKeySpki), { name: 'Ed25519' }, false, ['verify']);
    const ok = await crypto.subtle.verify('Ed25519', key, fromBase64(signature), new TextEncoder().encode(payload));
    if (!ok) return null;
    const parsed = JSON.parse(payload) as Record<string, unknown>;
    if (!Array.isArray(parsed.hosts) || !isInstant(parsed.issuedAt)) return null;
    const hosts = normaliseEdgeHosts(parsed.hosts as string[]);
    if (hosts === null) return null;
    if (parsed.v === 1) {
      return { v: 1, environment: null, hosts, updatedAt: parsed.issuedAt, issuedAt: parsed.issuedAt };
    }
    if (parsed.v !== EDGE_HOSTS_VERSION || !isInstant(parsed.updatedAt)) return null;
    const { environment } = parsed;
    if (typeof environment !== 'string' || environment.length === 0 || environment.length > ENVIRONMENT_MAX) return null;
    return { v: EDGE_HOSTS_VERSION, environment, hosts, updatedAt: parsed.updatedAt, issuedAt: parsed.issuedAt };
  } catch {
    return null;
  }
}
